import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { subscribeToPwaUpdate } from '../../services/pwaUpdateService';

/**
 * Persistent update banner that appears under the TopBar when a new PWA
 * service worker is installed and waiting to activate.
 *
 * Behavior (per the locked-in design):
 *   - Non-dismissable. No X button, no auto-dismiss timer.
 *   - Entire banner is the click target: tap anywhere → apply update.
 *   - Stays until clicked OR until a hard browser refresh activates the
 *     waiting SW out-of-band (in which case `needRefresh` flips false and
 *     the banner unmounts on the next render).
 *   - Slim, accent-colored bar — not a modal, not a toast.
 *
 * Lets the user finish what they're doing before the page reloads. A
 * brand-new banner is the only signal we surface for soft deploys; for
 * hard cutoffs the /ping lifeline (VersionCheckRunner) takes over and
 * forces a reload independent of this banner.
 */
export const UpdateAvailableBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const [state, setState] = useState(() => ({
    needRefresh: false,
    updateAndReload: async () => {
      window.location.reload();
    },
  }));
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    return subscribeToPwaUpdate(setState);
  }, []);

  if (!state.needRefresh) return null;

  const handleClick = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await state.updateAndReload();
    } catch {
      // updateAndReload should always end in a reload; if it doesn't,
      // fall back so the user isn't stuck.
      window.location.reload();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={t('ui.updateBanner.aria')}
      disabled={applying}
      className="update-banner"
    >
      <span>{t('ui.updateBanner.body')}</span>
      <span className="text-brand-primary font-medium">
        {applying ? t('ui.updateBanner.applying') : t('ui.updateBanner.cta')}
      </span>
    </button>
  );
};
